import React, { useState } from 'react'
import { Stack, Box, Card, Typography, IconButton, useMediaQuery, useTheme } from '@mui/material'
import { J1RightarrowD } from '@e3dc-react/icons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ChargingStateType, ManufacturerState } from '../../types'
import { isEmpty, isString } from 'lodash'
import VehicleStatus from './VehicleStatus'
import VehicleInformation from './VehicleInformation'
import { VehicleImage } from '../vehicle-image'
import RemoveVehicleModal from '../RemoveVehicleModal'
import VehicleTileActions from './VehicleTileActions'
import { UserActionRequiredErrorDto } from '../../dto'
import VinTile from '../vin-tile/VinTile'

export interface VehicleTileProps {
  onVehicleTileClick: () => void
  vehicleName?: string
  vehicleId: number
  imageUrl: string | null
  vehicleInformationHasError: boolean
  chargingState?: ChargingStateType
  estimatedRange?: number
  stateOfCharge?: number
  isOnline: boolean
  vendorState?: ManufacturerState
  vehicleAvailable?: boolean
  isVehicleConnectionInProgress: boolean
  isVehicleDisconnected: boolean
  installationId: string
  vin: string
  userActionRequiredError: UserActionRequiredErrorDto[] | null
}

/**
 * This component shows information about a vehicle in the vehicle list page.
 * It is clickable and opens the vehicle detail modal.
 */
const VehicleTile: React.FC<VehicleTileProps> = ({
  onVehicleTileClick,
  vehicleName,
  imageUrl,
  vehicleInformationHasError,
  chargingState,
  estimatedRange,
  stateOfCharge,
  isOnline,
  vendorState = ManufacturerState.temporarilyUnavailable,
  vehicleAvailable = false,
  isVehicleConnectionInProgress,
  isVehicleDisconnected,
  vehicleId,
  installationId,
  vin,
  userActionRequiredError,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { t } = useCustomTranslation()

  const [isRemoveVehicleModalOpen, setIsRemoveVehicleModalOpen] = useState(false)

  const hasVehicleName = isString(vehicleName) && !isEmpty(vehicleName)
  const isVehicleInformationAvailable = !(vehicleInformationHasError || !estimatedRange || !stateOfCharge)
  const hasVin = isString(vin) && !isEmpty(vin)

  const vehicleTileActions = (
    <VehicleTileActions
      vehicleId={vehicleId}
      installationId={installationId}
      setIsRemoveVehicleModalOpen={setIsRemoveVehicleModalOpen}
      hasVin={hasVin}
      isVehicleConnectionInProgress={isVehicleConnectionInProgress}
      isVehicleDisconnected={isVehicleDisconnected}
    />
  )

  return (
    <>
      <Card
        sx={{ cursor: isVehicleConnectionInProgress ? 'default' : 'pointer', p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        onClick={onVehicleTileClick}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', pb: 2 }}>
          <Stack flex={1}>
            <Typography variant="h3" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '40ch' }}>
              {hasVehicleName ? vehicleName : '---'}
            </Typography>
            {hasVin && (
              <Stack flexDirection="row" alignItems="center" gap={1}>
                <Typography variant="body2" color="grey.grey00">
                  VIN
                </Typography>
                <VinTile vin={vin} />
              </Stack>
            )}
            <Stack direction="row" alignItems="center">
              <VehicleStatus
                vehicleAvailable={vehicleAvailable}
                isOnline={isOnline}
                chargingState={chargingState}
                isVehicleConnectionInProgress={isVehicleConnectionInProgress}
                isVehicleDisconnected={isVehicleDisconnected}
              />
            </Stack>
          </Stack>
          {!isVehicleConnectionInProgress && !isVehicleDisconnected && (
            <IconButton>
              <J1RightarrowD sx={{ color: 'grey.grey00' }} />
            </IconButton>
          )}
          {!isMobile && vehicleTileActions}
        </Box>

        <Stack
          direction="row"
          justifyContent="center"
          sx={{ width: '100%', overflow: 'hidden', cursor: isVehicleConnectionInProgress ? 'default' : 'pointer' }}
        >
          <VehicleImage imageUrl={imageUrl} showImage={vehicleAvailable} />
        </Stack>

        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pt: 2, mt: 'auto', mb: 1 }}>
          <VehicleInformation
            vehicleAvailable={vehicleAvailable}
            isVehicleInformationAvailable={isVehicleInformationAvailable}
            estimatedRange={estimatedRange}
            stateOfCharge={stateOfCharge}
            vendorState={vendorState}
            isVehicleConnectionInProgress={isVehicleConnectionInProgress}
            isVehicleDisconnected={isVehicleDisconnected}
            vin={vin}
            userActionRequiredError={userActionRequiredError}
          />
        </Box>
        {isMobile && vehicleTileActions}
      </Card>
      {isRemoveVehicleModalOpen && (
        <RemoveVehicleModal
          title={t('emobility.vehicle-integration.charging-schedule.delete-vehicle')}
          description={t('emobility.vehicle-integration.remove-disconnected-vehicle-text')}
          installationId={Number(installationId)}
          vehicleId={vehicleId}
          open={isRemoveVehicleModalOpen}
          onClose={() => setIsRemoveVehicleModalOpen(false)}
        />
      )}
    </>
  )
}

export default VehicleTile
